import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Copy, Gift, Users, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import { BRAND } from '../data/brand';

const REWARD_TIERS = [
  { points: 0, label: 'New Member', perk: 'Welcome gift on first order' },
  { points: 500, label: 'Insider', perk: '5% off all orders' },
  { points: 1500, label: 'Inner Circle', perk: '10% off + early access' },
  { points: 5000, label: 'Keeper of Secrets', perk: '15% off + exclusive drops + free shipping' },
];

const HOW_IT_WORKS = [
  {
    icon: Users,
    title: 'Share Your Link',
    description: 'Every member gets a unique referral link. Share it with someone who appreciates the finer things.',
  },
  {
    icon: Gift,
    title: 'They Get a Gift',
    description: 'Your referral receives 15% off their first order. A proper introduction to the collection.',
  },
  {
    icon: Copy,
    title: 'You Earn Points',
    description: 'For every successful referral, you earn 250 points toward exclusive rewards and tier upgrades.',
  },
];

const RewardsPage = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const mockCode = 'SECRET13-MEMBER-7X9K';

  const handleCopy = () => {
    navigator.clipboard.writeText(mockCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            Rewards
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zinc-50 mb-6"
            data-testid="rewards-heading"
          >
            Share the Secret
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-sans text-sm text-zinc-500 max-w-lg mx-auto leading-relaxed"
          >
            The best things spread quietly. Refer someone worthy and earn your way to deeper rewards.
          </motion.p>
        </div>
      </section>

      {/* Referral Code */}
      <section className="py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="border border-white/10 p-6 text-center"
          >
            <p className="font-sans text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Your Referral Code</p>
            <div className="flex items-center justify-center gap-3">
              <p className="font-mono text-lg text-zinc-200 tracking-wider" data-testid="referral-code">{mockCode}</p>
              <button
                onClick={handleCopy}
                data-testid="copy-referral"
                className="text-zinc-500 hover:text-white transition-colors"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {copied && <p className="font-sans text-xs text-zinc-400 mt-2">Copied</p>}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4">How It Works</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-light text-zinc-50">Three Simple Steps</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {HOW_IT_WORKS.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.15 }}
                  className="text-center"
                  data-testid={`reward-step-${index}`}
                >
                  <Icon className="w-6 h-6 text-zinc-500 mx-auto mb-5" />
                  <h3 className="font-serif text-xl text-zinc-100 mb-3">{step.title}</h3>
                  <p className="font-sans text-sm text-zinc-500 leading-relaxed">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reward Tiers */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4">Point Tiers</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-light text-zinc-50">The Deeper You Go</h2>
          </motion.div>

          <div className="space-y-4">
            {REWARD_TIERS.map((tier, index) => (
              <motion.div
                key={tier.label}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-center justify-between border-b border-white/5 py-5"
                data-testid={`reward-tier-${index}`}
              >
                <div>
                  <p className="font-serif text-lg text-zinc-100">{tier.label}</p>
                  <p className="font-sans text-xs text-zinc-500">{tier.perk}</p>
                </div>
                <p className="font-sans text-sm text-zinc-400">{tier.points.toLocaleString()} pts</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-sans text-sm text-zinc-500 mb-6">Start earning. Start sharing.</p>
            <button
              data-testid="rewards-cta"
              onClick={() => navigate('/collection')}
              className="inline-flex items-center gap-3 bg-white text-black px-10 py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:bg-zinc-200 transition-all duration-300"
            >
              Explore Collection
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">{BRAND.legal.copyright} {BRAND.legal.membersOnly}</p>
        </div>
      </footer>
    </div>
  );
};

export default RewardsPage;
